/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, ViewEncapsulation, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NotificationService } from '@alfresco/adf-core';
import { NodesApiService, TagService } from '@alfresco/adf-content-services';
import { Node } from '@alfresco/js-api';
import { ThaiDatePipe } from '@alfresco/aca-shared';
import { DmsField, DmsMetadataService } from '../upload/dms-metadata.service';
import { FIELD_LABELS } from '../document-types/document-types.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AssistantService, ClassifyField } from '../assistant/assistant.service';

/**
 * Never filled by the AI: they decide who may read a document and where it is in
 * its life, so they are left to a person. The service refuses them too.
 */
const NEVER_SUGGESTED = new Set(['dms:confidentiality', 'dms:status']);

/** Which section of the panel is open. */
type Section = 'general' | 'tags' | 'classification' | 'exif';

/** One line of a section's body. */
interface Row {
  label: string;
  value: string;
  /** A field of the shared metadata schema, when the row edits one. */
  field?: DmsField;
  key?: string;
  wide?: boolean;
}

/**
 * The properties of one document (UI-20), laid out as the reference draws it:
 * a card per section, each with its own tile, a line summarising what it holds,
 * and an edit button that turns just that section into a form.
 *
 * ADF's metadata card was used here before. It owns a generic aspect editor,
 * but it draws a card holding an accordion holding one outlined field per
 * value, with no room for the summary lines the reference asks for. What this
 * application actually stores is known — the node's own fields, its tags and
 * the shared `dms:documentInfo` schema — so the panel is built from those.
 */
@Component({
  selector: 'app-document-properties',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, MatTooltipModule, ThaiDatePipe, TranslatePipe],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './document-properties.component.html',
  styleUrl: './document-properties.component.scss'
})
export class DocumentPropertiesComponent implements OnChanges {
  @Input({ required: true }) node!: Node;

  /**
   * The node the panel is drawing. It mirrors the input, and is a signal so the
   * rows below recompute when a save comes back with new values — assigning
   * over the input's own object would leave them showing what was there before.
   */
  readonly current = signal<Node | null>(null);

  private readonly nodes = inject(NodesApiService);
  private readonly tagService = inject(TagService);
  private readonly dms = inject(DmsMetadataService);
  private readonly notification = inject(NotificationService);
  private readonly translate = inject(TranslateService);
  private readonly assistant = inject(AssistantService);

  /**
   * Whether the AI suggestion is on. It follows the assistant's own switch, so a
   * company that turned AI off — or only this feature — never sees the button.
   */
  readonly canSuggest = computed(() => !!this.assistant.status()?.classify);
  readonly suggesting = signal(false);
  /** The fields the AI filled in this edit, marked until the person saves or cancels. */
  readonly suggested = signal<ReadonlySet<string>>(new Set());

  /** Which sections are open. The first one starts open, as the reference has it. */
  readonly open = signal<Record<Section, boolean>>({ general: true, tags: false, classification: false, exif: false });
  /** The section being edited, if any. */
  readonly editing = signal<Section | null>(null);
  readonly saving = signal(false);
  readonly fields = signal<Record<string, DmsField>>({});
  readonly tags = signal<{ id: string; tag: string }[]>([]);

  /** What the form holds while a section is open for editing. */
  draft: Record<string, string> = {};
  newTag = '';

  ngOnChanges(): void {
    if (!this.node?.id) {
      return;
    }
    this.current.set(this.node);
    this.editing.set(null);
    this.suggested.set(new Set());
    if (!this.assistant.status()) {
      this.assistant.refreshStatus();
    }
    this.loadTags();
    this.dms.schema().subscribe((schema) => this.fields.set(schema.available ? schema.fields : {}));
  }

  private loadTags(): void {
    this.tagService.getTagsByNodeId(this.node.id).subscribe({
      next: (paging: any) => this.tags.set((paging?.list?.entries ?? []).map((entry: any) => ({ id: entry.entry.id, tag: entry.entry.tag }))),
      error: () => this.tags.set([])
    });
  }

  // ── what each section holds ────────────────────────────────────────────────

  readonly generalRows = computed<Row[]>(() => {
    const node = this.current();
    return [
      { label: 'DMS.HISTORY.F_NAME', value: node?.name ?? '', key: 'cm:name' },
      { label: 'DMS.UI.EADFBF', value: (node?.properties?.['cm:title'] as string) ?? '', key: 'cm:title' },
      { label: 'DMS.UI.EB455F', value: node?.createdByUser?.displayName ?? '' },
      { label: 'DMS.UI.DC1FAC', value: node?.modifiedAt ? String(node.modifiedAt) : '' },
      { label: 'DMS.HISTORY.F_SIZE', value: this.sizeLabel },
      { label: 'DMS.HISTORY.F_NOTE', value: (node?.properties?.['cm:description'] as string) ?? '', key: 'cm:description', wide: true }
    ];
  });

  readonly classificationRows = computed<Row[]>(() => {
    const fields = this.fields();
    const node = this.current();
    const order = ['dms:documentType', 'dms:status', 'dms:confidentiality', 'dms:department', 'dms:documentCode', 'dms:documentOwner'];
    const known = new Set(order);
    const ids = [...order.filter((id) => fields[id]), ...Object.keys(fields).filter((id) => !known.has(id))];
    return ids.map((id) => ({
      // a field the model shipped with is named here; one added from the
      // settings screen has its name in the label store the metadata service reads
      label: FIELD_LABELS[id] ?? DmsMetadataService.label(id),
      value: String(node?.properties?.[id] ?? ''),
      field: fields[id],
      key: id
    }));
  });

  readonly exifRows = computed<Row[]>(() =>
    Object.entries(this.current()?.properties ?? {})
      .filter(([key, value]) => key.startsWith('exif:') && value !== null && value !== undefined && value !== '')
      .map(([key, value]) => ({ label: key.replace('exif:', ''), value: String(value) }))
  );

  // ── the line under each section's name ─────────────────────────────────────

  get sizeLabel(): string {
    const bytes = this.current()?.content?.sizeInBytes;
    if (bytes === undefined || bytes === null) {
      return '—';
    }
    if (bytes < 1024) {
      return `${bytes} B`;
    }
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(2)} KB`;
    }
    return `${(bytes / 1024 / 1024).toFixed(2)} MB (${bytes.toLocaleString('th-TH')} ${this.translate.instant('DMS.HISTORY.BYTES')})`;
  }

  /** The file's kind, shown as a chip beside its name. */
  get kindLabel(): string {
    const node = this.current();
    const mime = node?.content?.mimeType ?? '';
    const ext = (node?.name ?? '').split('.').pop() ?? '';
    return (mime.split('/').pop() || ext).toUpperCase().slice(0, 6);
  }

  get tagSummary(): string {
    const tags = this.tags();
    if (!tags.length) {
      return this.translate.instant('DMS.UI.8E116C');
    }
    return this.translate.instant('DMS.PREVIEW.TAG_COUNT', { p0: tags.length });
  }

  get classificationSummary(): string {
    const type = this.valueLabel('dms:documentType');
    const level = this.valueLabel('dms:confidentiality');
    if (!type && !level) {
      return this.translate.instant('DMS.PREVIEW.NO_CLASS');
    }
    return this.translate.instant('DMS.PREVIEW.CLASS_SUMMARY', {
      p0: type || '—',
      p1: level || '—'
    });
  }

  get exifSummary(): string {
    return this.translate.instant('DMS.PREVIEW.EXIF_COUNT', { p0: this.exifRows().length });
  }

  /** A stored code as its display text, for the summary lines. */
  valueLabel(id: string): string {
    const code = this.current()?.properties?.[id];
    if (!code) {
      return '';
    }
    const option = this.fields()[id]?.options?.find((entry) => entry.value === code);
    return this.translate.instant(option ? option.label : DmsMetadataService.label(String(code)));
  }

  /** What a row shows when the section is being read rather than written. */
  display(row: Row): string {
    if (!row.value) {
      return '—';
    }
    if (row.field?.options?.length) {
      const option = row.field.options.find((entry) => entry.value === row.value);
      return this.translate.instant(option ? option.label : row.value);
    }
    return row.value;
  }

  isDate(row: Row): boolean {
    return row.label === 'DMS.UI.DC1FAC' || row.field?.dataType === 'd:date' || row.field?.dataType === 'd:datetime';
  }

  // ── opening, editing and saving ────────────────────────────────────────────

  toggle(section: Section): void {
    this.open.update((state) => ({ ...state, [section]: !state[section] }));
  }

  startEdit(section: Section, rows: Row[], event: Event): void {
    event.stopPropagation();
    this.open.update((state) => ({ ...state, [section]: true }));
    this.editing.set(section);
    this.suggested.set(new Set());
    this.draft = {};
    for (const row of rows) {
      if (row.key) {
        this.draft[row.key] = row.value;
      }
    }
  }

  cancelEdit(): void {
    this.editing.set(null);
    this.suggested.set(new Set());
    this.draft = {};
  }

  /**
   * Asks the AI what this document's classification probably is and puts the
   * answer into the open form. It fills only fields that are still empty, and
   * marks them: nothing is saved until the person reads them and presses save.
   */
  suggest(): void {
    if (this.suggesting() || this.editing() !== 'classification') {
      return;
    }

    const fields: ClassifyField[] = [];
    for (const { key, field, label } of this.classificationRows()) {
      if (key && field && !NEVER_SUGGESTED.has(key)) {
        fields.push({
          id: key,
          label: this.translate.instant(label),
          dataType: field.dataType,
          options: field.options?.map((option) => ({ value: option.value, label: this.translate.instant(option.label) }))
        });
      }
    }

    this.suggesting.set(true);
    this.assistant.classify(this.node.id, fields).subscribe({
      next: (result) => {
        this.suggesting.set(false);
        if (this.editing() !== 'classification') {
          return;
        }

        // only what was asked about is taken, whatever comes back
        const asked = new Set(fields.map((field) => field.id));
        const filled = new Set<string>();
        let kept = 0;
        for (const [key, value] of Object.entries(result.fields ?? {})) {
          if (!asked.has(key) || !(key in this.draft)) {
            continue;
          }
          if (this.draft[key]) {
            kept++;
          } else {
            this.draft[key] = value;
            filled.add(key);
          }
        }

        this.suggested.set(filled);
        if (filled.size) {
          this.notification.showInfo(this.translate.instant('DMS.ASSISTANT.CLASSIFY_DONE', { filled: filled.size, kept }));
        } else {
          const reason = kept ? 'KEPT' : result.reason === 'no-text' ? 'NO_TEXT' : result.reason === 'confidentiality' ? 'WITHHELD' : 'NONE';
          this.notification.showWarning(this.translate.instant('DMS.ASSISTANT.CLASSIFY_' + reason));
        }
      },
      error: (error) => {
        this.suggesting.set(false);
        this.notification.showError(this.translate.instant(error?.status === 403 ? 'DMS.ASSISTANT.CLASSIFY_OFF' : 'DMS.ASSISTANT.CLASSIFY_FAIL'));
      }
    });
  }

  save(): void {
    if (this.saving()) {
      return;
    }
    this.saving.set(true);

    const properties: Record<string, any> = {};
    let name: string | undefined;
    for (const [key, value] of Object.entries(this.draft)) {
      if (key === 'cm:name') {
        name = value.trim();
      } else {
        properties[key] = value === '' ? null : value;
      }
    }

    const body: Record<string, any> = { properties };
    if (name && name !== this.current()?.name) {
      body['name'] = name;
    }

    this.nodes.updateNode(this.node.id, body as any).subscribe({
      next: (updated: Node) => {
        this.saving.set(false);
        this.editing.set(null);
        this.suggested.set(new Set());
        // the parent holds the same object, so it is kept in step as well
        Object.assign(this.node, updated);
        this.current.set({ ...updated });
        this.notification.showInfo(this.translate.instant('DMS.UI.005CB3'));
      },
      error: () => {
        this.saving.set(false);
        this.notification.showError(this.translate.instant('DMS.UI.582420'));
      }
    });
  }

  // ── tags ───────────────────────────────────────────────────────────────────

  addTag(): void {
    const name = this.newTag.trim();
    if (!name) {
      return;
    }
    this.tagService.addTag(this.node.id, name).subscribe({
      next: () => {
        this.newTag = '';
        this.loadTags();
      },
      error: () => this.notification.showError(this.translate.instant('DMS.HISTORY.TAG_FAIL'))
    });
  }

  removeTag(tag: { id: string; tag: string }): void {
    this.tagService.removeTag(this.node.id, tag.id).subscribe({
      next: () => this.loadTags(),
      error: () => this.notification.showError(this.translate.instant('DMS.UI.582420'))
    });
  }

  trackByKey(index: number, row: Row): string {
    return row.key ?? row.label + index;
  }
}
